import { Box, FocusTrap, Group, Paper, Stack, Text } from '@mantine/core'
import { IconPencil, IconTrash, IconX } from '@tabler/icons-react'
import React from 'react'
import { actions } from '#/actions'
import assetStore from '#/assetStore'
import ActionIcon from '#/components/common/ActionIcon'
import ButtonNew from '#/components/common/ButtonNew'
import ModalNew from '#/components/common/ModalNew'
import Avatar from '#/components/common/avatar'
import type { AssetResponse, PermissionBase, PermissionResponse } from '#/dataInterface'
import { router } from '#/router/legacy'
import { ROUTES } from '#/router/routerConstants'
import profileStore from '#/stores/profile'
import { KOBO_MODAL_OVERLAY_PROPS } from '#/theme/kobo/Modal'
import { KOBO_Z_INDEX } from '#/theme/kobo/zIndex'
import { permissionsActions } from '../../actions/permissions'
import permConfig from './permConfig'
import { PERMISSIONS_CODENAMES } from './permConstants'
import type { AssignablePermsMap } from './sharingForm.component'
import UserAssetPermsEditor from './userAssetPermsEditor.component'
import { getFriendlyPermName, getPermLabel } from './utils'

interface UserPermissionRowProps {
  asset: AssetResponse
  userCanEditPerms: boolean
  nonOwnerPerms: PermissionBase[]
  assignablePerms: AssignablePermsMap
  permissions: PermissionResponse[]
  isUserOwner: boolean
  isPendingOwner: boolean
  username: string
}

interface UserPermissionRowState {
  isEditFormVisible: boolean
  isBeingDeleted: boolean
  isRemovePermissionsPromptVisible: boolean
}

export default class UserPermissionRow extends React.Component<UserPermissionRowProps, UserPermissionRowState> {
  constructor(props: UserPermissionRowProps) {
    super(props)

    this.state = {
      isEditFormVisible: false,
      isBeingDeleted: false,
      isRemovePermissionsPromptVisible: false,
    }
  }

  componentDidMount() {
    assetStore.listen(this.onAssetChange, this)
  }

  onAssetChange() {
    // fixes bug that disables a user who was re-added after being deleted
    this.setState({ isBeingDeleted: false })
  }

  showRemovePermissionsPrompt() {
    this.setState({ isRemovePermissionsPromptVisible: true })
  }

  hideRemovePermissionsPrompt() {
    this.setState({ isRemovePermissionsPromptVisible: false })
  }

  removeAllPermissions() {
    this.setState({ isRemovePermissionsPromptVisible: false, isBeingDeleted: true })

    const isCurrentUser = this.props.username === profileStore.currentAccount.username

    actions.permissions.removeAssetPermission(this.props.asset.uid, undefined, true, undefined, this.props.username)

    permissionsActions.removeAssetPermission.completed.listen(() => {
      // If the user deletes their own permissions, they will be routed to the form landing page
      if (isCurrentUser) {
        router?.navigate(ROUTES.FORMS)
      }
    })
  }

  onPermissionsEditorSubmitEnd(isSuccess: boolean) {
    if (isSuccess) {
      this.setState({ isEditFormVisible: false })
    }
  }

  toggleEditForm() {
    this.setState({ isEditFormVisible: !this.state.isEditFormVisible })
  }

  /**
   * Note that this renders partial permission using a general label with a list
   * of related conditions.
   */
  renderPermissions(permissions: PermissionResponse[]) {
    const permissionLabels: string[] = []

    permissions.forEach((perm) => {
      // UI already shows if a collection is discoverable, and we should not explicitly assign this permission.
      if (perm.permission.includes(PERMISSIONS_CODENAMES.discover_asset)) {
        return
      }

      // Keep this as key generation and to preserve previous mapping behavior.
      getPermLabel(perm, this.props.asset.asset_type)

      // Between UserPermissionRow and UserAssetPermsEditor, generation of permission labels takes a small but
      // significantly different starting point. To avoid deeper complications we do a little bit of redundant work
      // here (to get the permission definition) needed to generate contextual labels.
      //
      // See https://github.com/kobotoolbox/kpi/pull/5736#discussion_r2085252485
      const permDef = permConfig.getPermission(perm.permission)
      if (permDef) {
        permissionLabels.push(getFriendlyPermName(perm, this.props.asset.asset_type))
      }
    })

    return (
      <Text size='sm' ta='right' maw={400}>
        {permissionLabels.join(' · ')}
      </Text>
    )
  }

  /**
   * A confirmation modal displayed on top of the Sharing modal, thus it needs the nested modal z-indexes.
   */
  renderRemovePermissionsPrompt() {
    const [messageStart, messageEnd] = t('This action will remove all permissions for user ##username##').split(
      '##username##',
    )

    return (
      <ModalNew
        opened={this.state.isRemovePermissionsPromptVisible}
        onClose={this.hideRemovePermissionsPrompt.bind(this)}
        title={t('Remove permissions?')}
        size='sm'
        zIndex={KOBO_Z_INDEX.nestedModal}
        overlayProps={{
          ...KOBO_MODAL_OVERLAY_PROPS,
          zIndex: KOBO_Z_INDEX.nestedModalOverlay,
        }}
      >
        {/* We don't want "x" button to get focus (see https://mantine.dev/core/modal/#initial-focus) */}
        <FocusTrap.InitialFocus />

        <Stack>
          <Text>
            {messageStart}
            <strong>{this.props.username}</strong>
            {messageEnd}
          </Text>

          <Group justify='flex-end'>
            <ButtonNew size='md' variant='light' onClick={this.hideRemovePermissionsPrompt.bind(this)}>
              {t('Cancel')}
            </ButtonNew>

            <ButtonNew size='md' variant='danger' onClick={this.removeAllPermissions.bind(this)}>
              {t('Remove')}
            </ButtonNew>
          </Group>
        </Stack>
      </ModalNew>
    )
  }

  render() {
    if (!this.props.isPendingOwner && this.props.permissions.length === 0) {
      return null
    }

    const isPending = this.state.isBeingDeleted

    return (
      <Paper withBorder p='sm'>
        {this.renderRemovePermissionsPrompt()}

        <Stack gap='xs'>
          <Group
            justify='space-between'
            align='center'
            wrap='nowrap'
            style={{ opacity: isPending ? 0.5 : 1, pointerEvents: isPending ? 'none' : 'auto' }}
          >
            <Box miw={200}>
              <Avatar size='m' username={this.props.username} isUsernameVisible />
            </Box>

            {this.props.isUserOwner && <Text size='sm'>{t('is owner')}</Text>}

            {this.props.isPendingOwner && <Text size='sm'>{t('Pending owner')}</Text>}

            {!this.props.isUserOwner && !this.props.isPendingOwner && (
              <Group gap='xs' align='center' wrap='nowrap'>
                {this.renderPermissions(this.props.permissions)}
                {this.props.userCanEditPerms && (
                  <Group gap={6} wrap='nowrap'>
                    <ActionIcon
                      variant='light'
                      size='md'
                      aria-label={this.state.isEditFormVisible ? t('Close permission editor') : t('Edit permissions')}
                      tooltip={this.state.isEditFormVisible ? t('Close permission editor') : t('Edit permissions')}
                      onClick={this.toggleEditForm.bind(this)}
                      icon={this.state.isEditFormVisible ? IconX : IconPencil}
                    />

                    <ActionIcon
                      variant='danger-secondary'
                      size='md'
                      color='red'
                      aria-label={t('Remove all permissions')}
                      tooltip={t('Remove all permissions')}
                      onClick={this.showRemovePermissionsPrompt.bind(this)}
                      icon={IconTrash}
                    />
                  </Group>
                )}
              </Group>
            )}
          </Group>

          {this.state.isEditFormVisible && (
            <Box p='md' bg='gray.7' bdrs='sm'>
              <UserAssetPermsEditor
                asset={this.props.asset}
                username={this.props.username}
                permissions={this.props.permissions}
                assignablePerms={this.props.assignablePerms}
                nonOwnerPerms={this.props.nonOwnerPerms}
                onSubmitEnd={this.onPermissionsEditorSubmitEnd.bind(this)}
              />
            </Box>
          )}
        </Stack>
      </Paper>
    )
  }
}
